import { useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { uploadFile } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import Modal from '../../../../components/modal/Modal'
import FormLoader from '../../../../components/loader/FormLoader'
import { PrecheckResult, PrecheckState } from './IntelligentCheck'
import './AgentKycForm.scss'

const ID_TYPES = [
  { value: 'nin_slip', label: 'NIN Slip' },
  { value: 'drivers_licence', label: "Driver's Licence" },
  { value: 'passport', label: 'International Passport' },
]

const ADDRESS_TYPES = [
  { value: 'utility_bill', label: 'Utility Bill' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'tenancy_agreement', label: 'Tenancy Agreement' },
]

const MAX_FILE_SIZE = 8 * 1024 * 1024

interface Props {
  userId: string
  customerName: string
  onClose: () => void
  onSuccess: () => void
}

interface PrecheckResponse {
  available: boolean
  verdict?: PrecheckState['verdict']
  issues?: PrecheckState['issues']
}

interface DocState {
  file: File | null
  preview: string | null
}

const AgentKycForm = ({ userId, customerName, onClose, onSuccess }: Props) => {
  const { notify } = useGlobalContext()
  const [nin, setNin] = useState('')
  const [idDocumentType, setIdDocumentType] = useState('')
  const [proofOfAddressType, setProofOfAddressType] = useState('')
  const [proofOfAddressDate, setProofOfAddressDate] = useState('')
  const [declaration, setDeclaration] = useState(false)
  const [loading, setLoading] = useState(false)
  const [docs, setDocs] = useState<{ [key: string]: DocState }>({
    idDocument: { file: null, preview: null },
    selfieWithId: { file: null, preview: null },
    proofOfAddress: { file: null, preview: null },
  })

  // Instant automated check of each picked image (advisory, never blocks
  // submit). A sequence number per field drops responses for a file that
  // has since been replaced or removed.
  const [prechecks, setPrechecks] = useState<{ [key: string]: PrecheckState | null }>({})
  const precheckSeq = useRef<{ [key: string]: number }>({})

  const runPrecheck = (key: string, file: File | null, documentType?: string) => {
    const seq = (precheckSeq.current[key] || 0) + 1
    precheckSeq.current[key] = seq
    const setResult = (value: PrecheckState | null) => {
      if (precheckSeq.current[key] === seq)
        setPrechecks((prev) => ({ ...prev, [key]: value }))
    }

    const type =
      documentType ??
      (key === 'idDocument'
        ? idDocumentType
        : key === 'proofOfAddress'
          ? proofOfAddressType
          : undefined)
    // The ID and address checks need the declared type — they run once it's picked.
    if (!file || (key !== 'selfieWithId' && !type)) {
      setResult(null)
      return
    }

    const formData = new FormData()
    formData.append('document', file)
    formData.append('image', key)
    if (type) formData.append('documentType', type)
    if (/^\d{11}$/.test(nin)) formData.append('nin', nin)
    if (key === 'proofOfAddress' && proofOfAddressDate)
      formData.append('proofOfAddressDate', proofOfAddressDate)

    setResult({ status: 'checking' })
    uploadFile(formData, `kyc/admin/precheck/${userId}`)
      .then((res: { success?: boolean; data?: PrecheckResponse }) => {
        if (res.success && res.data?.available) {
          setResult({
            status: 'done',
            verdict: res.data.verdict,
            issues: res.data.issues || [],
          })
        } else setResult(null)
      })
      .catch(() => setResult(null))
  }

  const setDoc = (key: string, file: File | null) => {
    if (file && !file.type.startsWith('image/')) {
      notify(
        'error',
        'PDFs are not supported — convert the document to an image (a screenshot works) and upload that instead',
      )
      return
    }
    if (file && file.size > MAX_FILE_SIZE) {
      notify('error', 'File must not exceed 8MB')
      return
    }
    setDocs((prev) => ({
      ...prev,
      [key]: {
        file,
        preview:
          file && file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : null,
      },
    }))
    runPrecheck(key, file)
  }

  const threeMonthsAgo = new Date(Date.now() - 92 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0]
  const today = new Date().toISOString().split('T')[0]

  const submit = () => {
    const errors: string[] = []
    if (!/^\d{11}$/.test(nin)) errors.push('a valid 11-digit NIN')
    if (!idDocumentType) errors.push('ID document type')
    if (!docs.idDocument.file) errors.push('ID document file')
    if (!docs.selfieWithId.file) errors.push('selfie with ID')
    if (!proofOfAddressType) errors.push('proof of address type')
    if (!proofOfAddressDate) errors.push('proof of address date')
    if (!docs.proofOfAddress.file) errors.push('proof of address file')
    if (!declaration) errors.push('the customer declaration')
    if (errors.length > 0) {
      notify('error', `Missing: ${errors.join(', ')}`)
      return
    }

    const formData = new FormData()
    formData.append('nin', nin)
    formData.append('idDocumentType', idDocumentType)
    formData.append('proofOfAddressType', proofOfAddressType)
    formData.append('proofOfAddressDate', proofOfAddressDate)
    formData.append('declarationAccepted', 'true')
    formData.append('idDocument', docs.idDocument.file!)
    formData.append('selfieWithId', docs.selfieWithId.file!)
    formData.append('proofOfAddress', docs.proofOfAddress.file!)

    setLoading(true)
    uploadFile(formData, `kyc/admin/submit/${userId}`)
      .then((res: any) => {
        if (res.success) {
          notify('success', 'KYC submitted for review')
          onClose()
          onSuccess()
        } else {
          notify(
            'error',
            typeof res.message === 'string' ? res.message : 'Submission failed',
          )
        }
        setLoading(false)
      })
      .catch(() => {
        notify('error', 'Submission failed')
        setLoading(false)
      })
  }

  const FileDrop = ({
    docKey,
    label,
    hint,
    icon,
  }: {
    docKey: string
    label: string
    hint: string
    icon: string
  }) => {
    const doc = docs[docKey]
    return (
      <div className='file-drop-group'>
        <label className='group-label'>{label}</label>
        <input
          type='file'
          id={`kyc-${docKey}`}
          accept='image/jpeg,image/png,image/webp'
          style={{ display: 'none' }}
          onChange={(e) => setDoc(docKey, e.target.files?.[0] || null)}
        />
        {doc.file ? (
          <div className='file-selected'>
            {doc.preview ? (
              <img src={doc.preview} alt={label} />
            ) : (
              <div className='pdf-chip'>
                <Icon icon='mdi:file-pdf-box' />
              </div>
            )}
            <div className='file-info'>
              <p className='name'>{doc.file.name}</p>
              <p className='size'>{(doc.file.size / 1024).toFixed(1)} KB</p>
            </div>
            <button className='remove' onClick={() => setDoc(docKey, null)}>
              <Icon icon='mdi:close' />
            </button>
          </div>
        ) : (
          <label htmlFor={`kyc-${docKey}`} className='file-drop'>
            <Icon icon={icon} />
            <p>{hint}</p>
            <span>JPG or PNG only · max 8MB · convert PDFs to images first</span>
          </label>
        )}
        {doc.file && <PrecheckResult state={prechecks[docKey]} />}
      </div>
    )
  }

  return (
    <Modal position='center'>
      <div className='agent-kyc-form'>
        <div className='form-header'>
          <div>
            <h2>Fill KYC for Customer</h2>
            <p>
              Submitting identity verification on behalf of <b>{customerName}</b>
            </p>
          </div>
          <button className='close-btn' onClick={onClose}>
            <Icon icon='mdi:close' />
          </button>
        </div>

        <div className='agent-banner'>
          <Icon icon='mdi:account-tie' />
          <p>
            This submission will be recorded under your name and reviewed by
            another admin — you will not be able to approve it yourself.
          </p>
        </div>

        <div className='form-body'>
          <div className='input-group'>
            <label className='group-label'>
              National Identification Number (NIN)
            </label>
            <input
              type='text'
              inputMode='numeric'
              maxLength={11}
              placeholder='11-digit NIN'
              value={nin}
              onChange={(e) => setNin(e.target.value.replace(/\D/g, ''))}
            />
            <span className='hint'>{nin.length}/11 digits</span>
          </div>

          <div className='input-group'>
            <label className='group-label'>ID Document Type</label>
            <div className='type-pills'>
              {ID_TYPES.map((t) => (
                <button
                  key={t.value}
                  className={idDocumentType === t.value ? 'active' : ''}
                  onClick={() => {
                    if (t.value === idDocumentType) return
                    setIdDocumentType(t.value)
                    if (docs.idDocument.file)
                      runPrecheck('idDocument', docs.idDocument.file, t.value)
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <FileDrop
            docKey='idDocument'
            label='Photo of ID Document'
            hint='Upload the customer’s ID'
            icon='mdi:card-account-details-outline'
          />
          <FileDrop
            docKey='selfieWithId'
            label='Selfie Holding the ID'
            hint='Photo of the customer holding their ID'
            icon='mdi:account-box-outline'
          />

          <div className='input-group'>
            <label className='group-label'>Proof of Address Type</label>
            <div className='type-pills'>
              {ADDRESS_TYPES.map((t) => (
                <button
                  key={t.value}
                  className={proofOfAddressType === t.value ? 'active' : ''}
                  onClick={() => {
                    if (t.value === proofOfAddressType) return
                    setProofOfAddressType(t.value)
                    if (docs.proofOfAddress.file)
                      runPrecheck('proofOfAddress', docs.proofOfAddress.file, t.value)
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className='input-group'>
            <label className='group-label'>Document Date</label>
            <input
              type='date'
              min={threeMonthsAgo}
              max={today}
              value={proofOfAddressDate}
              onChange={(e) => setProofOfAddressDate(e.target.value)}
            />
            <span className='hint'>Must be within the last 3 months</span>
          </div>

          <FileDrop
            docKey='proofOfAddress'
            label='Proof of Address Document'
            hint='Utility bill, bank statement or tenancy agreement'
            icon='mdi:home-city-outline'
          />

          <label className='declaration'>
            <input
              type='checkbox'
              checked={declaration}
              onChange={(e) => setDeclaration(e.target.checked)}
            />
            <span>
              I confirm the customer has agreed to QC Express's prohibited items
              policy and consented to this identity verification being submitted
              on their behalf.
            </span>
          </label>
        </div>

        {loading ? (
          <FormLoader position='center' />
        ) : (
          <div className='form-actions'>
            <button className='btn-cancel' onClick={onClose}>
              Cancel
            </button>
            <button className='btn-submit' onClick={submit}>
              <Icon icon='mdi:shield-check-outline' /> Submit for Review
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default AgentKycForm
